import { Query } from '@angular/core';
import { ECharts, EChartsOption } from 'echarts';
import { YAXisOption } from 'echarts/types/dist/shared';
import { IEChartsDashboardItemPersistentOptions } from 'src/app/components/echarts-dashboard-item/echarts-dashboard-item-persistent-options.model';
import { IEChartsDashboardItem } from 'src/app/components/echarts-dashboard-item/echarts-dashboard-item.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import {
    IQueryContext,
    isSplitApplied,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { distinctValues } from 'src/app/utils/array';
import {
    DashboardItemComponentType,
    DashboardItemFactory,
} from 'src/app/utils/dashboard-item';
import {
    StackingMode,
    applyStackingMode,
} from 'src/app/utils/echarts/apply-stacking-mode';
import {
    GLOBAL_CHART_OPTION,
    GLOBAL_LINE_CHART_OPTION,
    GLOBAL_TIME_XAXIS_OPTION,
    GLOBAL_TIME_YAXIS_OPTION,
    composeBaseDataset,
    composeSplitValueDataset,
    composeSplitValueSeries,
} from 'src/app/utils/echarts/echarts';
import {
    BITooltipOptions,
    EChartsTooltipHelper,
} from 'src/app/utils/echarts/echarts-tooltip-helper';
import {
    CategoryLabelSortFn,
    XYChartAxis,
} from 'src/app/utils/echarts/xy-chart-axis';
import { datetimePartDateTrunc } from 'src/app/utils/sql';

export interface XYChartDashoardItemFactoryOptions {
    /** Set xAxisType. Default is 'time'. It can be 'time', 'value', 'category'. It will set xAxis type to
     *   value for numeric value, time for time value, category for category value, like string
     */
    xAxisType?: 'time' | 'value' | 'category';
    /** Subtype of category xAxis. It is required when xAxisType is 'category' */
    xAxisCategorySubtype?: 'datetime' | 'generic';
    /** Key of datetime value column for category xAxis subtype 'datetime'. Target column can be string or number (unix timestamp) */
    // xAxisCategoryDatetimeKey?: string;
    dashboardName: string;
    createdInstanceId: string;
    dashboardItem: {
        title: string | ((context: IQueryContext) => string);
        subtitle?: string;
        /** A HTML string that contains documentation for the chart */
        chartDocumentation?: string;
    };
    /** Column label of XAxis
     */
    xColumnLabel: string;
    /** Return filter function for XAxis labels. It is used to filter category xAxis labels. */
    xAxisCategoryDatetimeFilterGetter?: (
        context: IQueryContext
    ) => (l: any) => boolean;
    /** Sort key for XAxis labels. It is used to sort category xAxis labels. */
    xAxisSortKey?: string;
    /** Sort function for XAxis labels. It is used to sort category xAxis labels. */
    xAxisSortFn?: CategoryLabelSortFn;
    /** X-axis label rotation
     *
     * Defines the rotation applied to X-axis labels.
     * Use `'90deg'` to display labels vertically.
     * @default 'none'
     */
    xAxisLabelRotation?: 'none' | '90deg';
    /** Setup Echarts yAxisLabel property.
     * It is used to set formatter for YAxis and many other properties
     * @see https://echarts.apache.org/en/option.html#yAxis.axisLabel
     */
    yAxisLabel?: YAXisOption['axisLabel'];
    // yColumnLabel can string or function
    /** Key of data */
    yColumnLabel?: string | ((context: IQueryContext) => string);
    /** Define how to get metric name. Must be defined with yColumnLabel */
    metricName?: string | ((context: IQueryContext) => string);
    metricsGetter?: (
        context: IQueryContext
    ) => Record<string, { echartsSeriesOption: {}; name: string }>;
    /** Chart type
     * @default 'line'
     */
    echartsSeriesType?: 'line' | 'bar';
    stackingMode?: StackingMode;
    colorGetterBuilder?: (
        context: IQueryContext
    ) => (splitValue: string) => { color: string };
    /**
     * @deprecated Use echartsOptionsOverride instead
     * Override top level echarts options
     */
    getEchartsOptionsOverride?: (
        context: IQueryContext,
        echartInstance: ECharts
    ) => EChartsOption;
    /** Function to override existing echart options
     * @deprecated Use tooltip
     */
    echartsOptionsOverride?: (
        context: IQueryContext,
        currentOptions: EChartsOption,
        echartsInstance: ECharts
    ) => void;
    /** Echarts tooltip. If exists, it will use EchartsHelper.createDefaultTooltip with provided options */
    tooltip?: {
        /** Function that returns formatter for X axis label in tooltip */
        xLabelFormatterGetter?: (
            context: IQueryContext
        ) => BITooltipOptions['xLabelFormatter'];
        /** Function that returns formatter for value in tooltip */
        valueFormatterGetter?: (
            context: IQueryContext
        ) => BITooltipOptions['valueFormatter'];
        /** Use it to add additional metrics to tooltip */
        secondaryMetricsGetter?: (
            context: IQueryContext
        ) => BITooltipOptions['secondaryMetrics'];
        /** Split name truncate limit, default 200 */
        splitNameTruncateLimitGetter?: (
            context: IQueryContext
        ) => BITooltipOptions['splitNameTruncateLimit'];
    };
}

export class XYChartDashoardItemFactory extends DashboardItemFactory<
    IEChartsDashboardItem,
    XYChartDashoardItemFactoryOptions
> {
    override FACTORY_CLASS: string = 'XYChartDashoardItemFactory';
    override COMPONENT_CLASS: DashboardItemComponentType =
        'EchartsDashboardItemComponent';

    override getInstance(options: XYChartDashoardItemFactoryOptions) {
        const echartsDashboardItem: IEChartsDashboardItem = {
            dashboardName: options.dashboardName,
            instanceId: options.createdInstanceId,
            dashboardItem: options.dashboardItem,
            title: options.dashboardItem.title,
            subtitle: options.dashboardItem.subtitle,
            chartDocumentation: options.dashboardItem.chartDocumentation,
            component: this.COMPONENT_CLASS,
            composeData: XYChartDashoardItemFactory.createComposeData(),
            initFactoryOptions: {
                ...options,
            },
        };

        return echartsDashboardItem;
    }

    /**
     * Create composeData function. Funtion that will be called to compose option object for echarts
     * @param options
     * @returns
     */
    static createComposeData() {
        return (
            data: any[],
            context?: IQueryContext,
            echartsInstance?: ECharts,
            options?: XYChartDashoardItemFactoryOptions
        ) => {
            // verify
            if (!context) {
                throw Error('Context is undefined');
            }
            if (!options) {
                throw Error('Options is undefined');
            }

            // default
            options.echartsSeriesType = options.echartsSeriesType ?? 'line';

            // xColumnLabel must be present in data
            if (data.length > 0 && !(options.xColumnLabel in data[0])) {
                throw Error(
                    `xColumnLabel: '${options.xColumnLabel}' not found in data`
                );
            }

            const metrics = (() => {
                // if yColumnLabel and metricName are undefined, then metricsGetter must be defined
                if (!options.yColumnLabel && !options.metricName) {
                    if (!options.metricsGetter) {
                        throw Error(
                            'If both yColumnLabel and metricName are undefined then metricsGetter must be defined'
                        );
                    }
                }
                // if metricsGetter is undefined, then yColumnLabel and metricName must be defined
                if (!options.metricsGetter) {
                    if (!options.yColumnLabel || !options.metricName) {
                        throw Error(
                            'If metricsGetter is undefined then both yColumnLabel and metricName must be defined'
                        );
                    }
                }

                const defaultMetricsGetter = (
                    context: IQueryContext
                ): Record<
                    string,
                    { echartsSeriesOption: {}; name: string }
                > => {
                    const metrics: Record<
                        string,
                        { echartsSeriesOption: {}; name: string }
                    > = {};
                    const metricName =
                        typeof options.metricName === 'function'
                            ? options.metricName(context)
                            : options.metricName!;

                    // if options.yColumnLabel is function, then call it, otherwise use options.
                    const yColumnLabel =
                        typeof options.yColumnLabel === 'function'
                            ? options.yColumnLabel(context)
                            : options.yColumnLabel!;

                    metrics[yColumnLabel] = {
                        echartsSeriesOption: {},
                        name: metricName,
                    };
                    return metrics;
                };

                const metrics = options.metricsGetter
                    ? options.metricsGetter(context)
                    : defaultMetricsGetter(context);

                return metrics;
            })();

            // const pivotResult = pivotTable(rawData);
            // console.log(pivotResult);
            let dataset: any = composeBaseDataset(data, context);

            let series: any = {};
            series.series = Object.keys(metrics).map((key: string) => {
                return {
                    ...{
                        type: options.echartsSeriesType,
                        // showSymbol: false,
                        name: metrics[key].name,
                        encode: { x: options.xColumnLabel, y: key },
                        lineStyle: { width: 1 },
                        color: ChartColorService.getDefaultColor(),
                    },
                    ...metrics[key].echartsSeriesOption,
                    // generate symbol callback or empty object
                    ...(() => {
                        if (!isSplitApplied(context)) {
                            return {
                                symbol: (value: any, params: any) => {
                                    const index = params.dataIndex;

                                    const pre =
                                        index > 0 ? data[index - 1][key] : null;
                                    const post =
                                        index < data.length - 1
                                            ? data[index + 1][key]
                                            : null;
                                    if (
                                        pre === null &&
                                        post === null &&
                                        value[key] !== null
                                    ) {
                                        return 'circle';
                                    }
                                    return 'none';
                                },
                            };
                        }
                        return {};
                    })(),
                };
            });

            if (isSplitApplied(context)) {
                const splitGroupValues: string[] = distinctValues(
                    dataset.dataset[0].source.map(
                        (item: any) => item.split_group
                    )
                );
                // console.log({ msg: "dau.data_processor.split_group.splitValueDataset", splitValueDataset });
                dataset.dataset = dataset.dataset.concat(
                    composeSplitValueDataset(splitGroupValues)
                );
                // console.log({ msg: "dau.data_processor.split_group.dataset", dataset: dataset.dataset });

                series = composeSplitValueSeries(
                    splitGroupValues,
                    series.series,
                    options.colorGetterBuilder
                        ? options.colorGetterBuilder(context)
                        : () => ({})
                );
            }

            // process series symbol
            let processSeriesSymbol = () => {
                if (!isSplitApplied(context)) {
                    series.series.forEach((s: any) => {
                        const key = s.encode.y;
                        s.symbol = (value: any, params: any) => {
                            const index = params.dataIndex;

                            const pre = index > 0 ? data[index - 1][key] : null;
                            const post =
                                index < data.length - 1
                                    ? data[index + 1][key]
                                    : null;
                            if (
                                pre === null &&
                                post === null &&
                                value[key] !== null
                            ) {
                                return 'circle';
                            }
                            return 'none';
                        };
                    });
                }
                if (isSplitApplied(context)) {
                    // create view for split group data
                    const splitGroupData = series.series
                        // get unique split group values
                        .map((s: any) => s.datasetId)
                        .filter(
                            (value: any, index: any, self: any) =>
                                self.indexOf(value) === index
                        )
                        // map to data
                        .map((splitGroup: any) => {
                            return {
                                id: splitGroup,
                                source: data.filter(
                                    (item: any) =>
                                        item.split_group === splitGroup
                                ),
                            };
                        })
                        // reduce to object
                        .reduce((acc: any, splitGroup: any) => {
                            acc[splitGroup.id] = splitGroup.source;
                            return acc;
                        }, {});

                    series.series.forEach((s: any) => {
                        // modify tooltip so that it shows split group
                        // s.encode.tooltip = [
                        //     'split_group',
                        //     options.xColumnLabel,
                        //     s.encode.y,
                        // ];

                        const metricKey = s.encode.y;
                        const splitGroupKey = s.datasetId;

                        // generate symbol callback or empty object
                        s.symbol = (value: any, params: any) => {
                            try {
                                if (value[metricKey] === null) {
                                    return 'none';
                                }
                                const index = params.dataIndex;

                                // value of previous and next data
                                const pre =
                                    index > 0
                                        ? splitGroupData[splitGroupKey][
                                              index - 1
                                          ][metricKey]
                                        : null;
                                const post =
                                    index <
                                    splitGroupData[splitGroupKey].length - 1
                                        ? splitGroupData[splitGroupKey][
                                              index + 1
                                          ][metricKey]
                                        : null;
                                if (
                                    pre === null &&
                                    post === null &&
                                    value[metricKey] !== null
                                ) {
                                    return 'circle';
                                }
                                return 'none';
                            } catch (error) {
                                console.log(error);
                                throw error;
                            }
                        };
                    });
                }
            };
            processSeriesSymbol();

            const baseline: EChartsOption = {
                legend: { show: false },
                // tooltip: {
                //     trigger: 'axis',
                //     valueFormatter:
                //         EChartsTooltipHelper.tooltipNumberValueFormatter,
                //     appendToBody: true,
                // },
            };

            // setup xAxisOption
            // if options.xAxis is undefined, then use GLOBAL_TIME_XAXIS_OPTION
            options.xAxisType = options.xAxisType ?? 'time';
            const xAxisOption: EChartsOption = ((
                xAxisType: XYChartDashoardItemFactoryOptions['xAxisType']
            ) => {
                switch (xAxisType) {
                    case 'time':
                        return GLOBAL_TIME_XAXIS_OPTION;
                    case 'value':
                        return {
                            xAxis: { type: 'value' },
                        };
                    case 'category':
                        return {
                            xAxis: {
                                type: 'category',
                                // default value is true, but set it explicitly
                                boundaryGap: true,
                                // align ticks with labels
                                axisTick: {
                                    show: true,
                                    alignWithLabel: true,
                                },
                            },
                        };
                    default:
                        throw Error(`Unsupported xAxisType: ${xAxisType}`);
                }
            })(options.xAxisType);

            if (echartsInstance === undefined) {
                throw Error('echartsInstance is undefined');
            }

            const echartsOptionsOverride = options.getEchartsOptionsOverride
                ? options.getEchartsOptionsOverride(context, echartsInstance)
                : undefined;

            let chartOption: EChartsOption = {
                ...GLOBAL_CHART_OPTION,
                ...GLOBAL_LINE_CHART_OPTION,
                ...xAxisOption,
                ...GLOBAL_TIME_YAXIS_OPTION,
                ...dataset,
                ...series,
                ...baseline,
                ...echartsOptionsOverride,
            };

            if (options.tooltip) {
                chartOption.tooltip = EChartsTooltipHelper.createDefaultTooltip(
                    echartsInstance,
                    {
                        xLabelFormatter:
                            options.tooltip.xLabelFormatterGetter?.(context),
                        valueFormatter:
                            options.tooltip.valueFormatterGetter?.(context),
                        secondaryMetrics:
                            options.tooltip.secondaryMetricsGetter?.(context),
                        splitNameTruncateLimit:
                            options.tooltip.splitNameTruncateLimitGetter?.(
                                context
                            ),
                    }
                );
            }

            // Apply x axis label rotation if enabled
            if (options.xAxisLabelRotation === '90deg') {
                chartOption.grid = {
                    ...chartOption.grid,
                    bottom: '3%',
                };
                chartOption.xAxis = {
                    ...chartOption.xAxis,
                    axisLabel: {
                        // @ts-ignore
                        ...chartOption.xAxis.axisLabel,
                        rotate: 90,
                    },
                };
            }

            // Apply custom values for time charts
            if (options.xAxisType === 'time') {
                // throw new Warning if options.xAxisSortKey or options.xAxisSortFn is defined
                if (options.xAxisSortKey || options.xAxisSortFn) {
                    console.warn(
                        'xAxisSortKey and xAxisSortFn are ignored for time xAxisType'
                    );
                }
                const datetimeGranularity: datetimePartDateTrunc =
                    context.settings[QueryContextSettingID.DATE_AGGREGATION] ??
                    context.settings[
                        QueryContextSettingID.DATETIME_AGGREGATION
                    ] ??
                    'day';
                XYChartAxis.applyEchartsTimeXAxisTicks(
                    chartOption,
                    options.xColumnLabel,
                    datetimeGranularity
                );
            }
            // Apply custom values for category charts
            else if (options.xAxisType === 'category') {
                // xAxisSubtype is required
                if (options.xAxisCategorySubtype === undefined) {
                    throw new Error(
                        'xAxisCategorySubtype is required for category xAxisType'
                    );
                }
                switch (options.xAxisCategorySubtype) {
                    case 'datetime':
                        var xAxisFilter: ((l: any) => boolean) | undefined =
                            undefined;
                        if (options.xAxisCategoryDatetimeFilterGetter) {
                            xAxisFilter =
                                options.xAxisCategoryDatetimeFilterGetter(
                                    context
                                );
                        }
                        XYChartAxis.applyEchartsCategoryXAxis(
                            chartOption,
                            options.xAxisCategorySubtype,
                            options.xColumnLabel,
                            options.xAxisSortKey,
                            options.xAxisSortFn,
                            {
                                enabled: true,
                                // datetimeKey: options.xAxisCategoryDatetimeKey!,
                                datetimeGranularity:
                                    context.settings[
                                        QueryContextSettingID
                                            .DATETIME_AGGREGATION
                                    ] ?? 'day',
                                axisDataFilter: xAxisFilter,
                            }
                        );
                        break;
                    case 'generic':
                        XYChartAxis.applyEchartsCategoryXAxis(
                            chartOption,
                            options.xAxisCategorySubtype,
                            options.xColumnLabel,
                            options.xAxisSortKey,
                            options.xAxisSortFn
                        );
                        break;
                    default:
                        throw new Error(
                            `Unsupported xAxisCategorySubtype: ${options.xAxisCategorySubtype}`
                        );
                }
            }

            // override existing echart options inplace
            if (options.echartsOptionsOverride) {
                options.echartsOptionsOverride(
                    context,
                    chartOption,
                    echartsInstance
                );
            }

            if (options.yAxisLabel) {
                if (!chartOption.yAxis) {
                    chartOption.yAxis = {};
                }
                if ('axisLabel' in chartOption.yAxis) {
                    throw new Error('yAxis.axisLabel already defined:');
                }
                // @ts-ignore
                chartOption.yAxis = {
                    ...chartOption.yAxis,
                    ...{ axisLabel: options.yAxisLabel },
                };
            }

            if (options.stackingMode && options.stackingMode !== 'none') {
                chartOption = applyStackingMode(
                    options.stackingMode,
                    chartOption,
                    echartsInstance,
                    options,
                    context,
                    () => {
                        processSeriesSymbol();
                    }
                );
            }

            // console.log({ msg: 'chartOption', chartOption });
            return chartOption;
        };
    }

    static effectiveOptions(
        initOptions: XYChartDashoardItemFactoryOptions,
        persistentOptions: IEChartsDashboardItemPersistentOptions
    ): XYChartDashoardItemFactoryOptions {
        return {
            ...initOptions,
            echartsSeriesType:
                initOptions.echartsSeriesType ?? persistentOptions.seriesType,
            stackingMode:
                initOptions.stackingMode ?? persistentOptions.stackingMode,
        };
    }
}
